import { Button, Group, Paper, Stack, Text, Textarea, Title } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import useCheckMobileScreen from '@/hooks/useCheckMobileScreen'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { GetCurrentSponsorPlaceholdersResponse } from '@/services/api/api.types'
import { resolveSponsorNamePlaceholder } from '@/utils/sponsors'

interface DraftWelcomeMessagePanelProps {
  showTutorialText?: boolean
  registerUnsavedChangesChecker: (tabKey: 'draftWelcomeMessage', checker: () => boolean, onDiscard: () => void) => void
}

export function DraftWelcomeMessagePanel({
  showTutorialText = false,
  registerUnsavedChangesChecker,
}: DraftWelcomeMessagePanelProps) {
  const { api } = useGlobalState()
  const isMobile = useCheckMobileScreen()
  const [isPending, setIsPending] = useState(false)

  // Subscriber welcome message lives in the placeholder terminology blob and is
  // persisted via /sponsor/update placeholders_values.member_introduction.
  const placeholderTerminologyQuery = useQuery({
    queryKey: ['terminology', 'placeholder'],
    queryFn: () => api.getPlaceholderTerminology(),
    refetchOnMount: 'always',
    staleTime: 0,
  })

  // Sponsor info gives us both the guest-access flag (decides whether to render
  // the guest field) and the guest welcome message itself, persisted via
  // /sponsor/update guest_user_message. Other consumers of this query key
  // (e.g. useGuestUserAccess) share this cache entry; since we want fresh data
  // when entering this panel we override staleTime locally.
  const sponsorInfoQuery = useQuery({
    queryKey: ['sponsor', 'info'],
    queryFn: () => api.getSponsorInfo(),
    refetchOnMount: 'always',
    staleTime: 0,
  })

  const guestUserAccess = sponsorInfoQuery.data?.guestUserAccess ?? false

  const [originalTerms, setOriginalTerms] = useState<GetCurrentSponsorPlaceholdersResponse | null>()
  const [terms, setTerms] = useState<GetCurrentSponsorPlaceholdersResponse | null>()
  const [originalGuestMessage, setOriginalGuestMessage] = useState<string>('')
  const [guestMessage, setGuestMessage] = useState<string>('')

  const allQueriesCompleted = placeholderTerminologyQuery.isSuccess && sponsorInfoQuery.isSuccess
  const anyQueryError = placeholderTerminologyQuery.isError || sponsorInfoQuery.isError
  const isLoading = placeholderTerminologyQuery.isLoading || sponsorInfoQuery.isLoading

  // Hydrate local form state from server data exactly once. Subsequent
  // refetches (e.g. from a background invalidation elsewhere) must not clobber
  // the user's in-flight edits. The refs reset on unmount, so re-entering the
  // panel re-hydrates from the freshly-refetched data.
  // Gate on both queries: the subscriber message lives in the terminology blob
  // but the channel name we substitute into it comes from sponsor info. Waiting
  // for both avoids hydrating with an unresolved token if terminology lands first.
  const termsHydrated = useRef(false)
  useEffect(() => {
    if (placeholderTerminologyQuery.data && sponsorInfoQuery.data && !termsHydrated.current) {
      termsHydrated.current = true
      const raw = placeholderTerminologyQuery.data
      const resolved = {
        ...raw,
        memberIntroduction: raw.memberIntroduction
          ? resolveSponsorNamePlaceholder(raw.memberIntroduction, sponsorInfoQuery.data.name)
          : raw.memberIntroduction,
      }
      setOriginalTerms(resolved)
      setTerms(resolved)
    }
  }, [placeholderTerminologyQuery.data, sponsorInfoQuery.data])

  const guestHydrated = useRef(false)
  useEffect(() => {
    if (sponsorInfoQuery.data && !guestHydrated.current) {
      guestHydrated.current = true
      const next = resolveSponsorNamePlaceholder(
        sponsorInfoQuery.data.guestUserMessage ?? '',
        sponsorInfoQuery.data.name,
      )
      setOriginalGuestMessage(next)
      setGuestMessage(next)
    }
  }, [sponsorInfoQuery.data])

  const subscriberMessage = terms?.memberIntroduction || ''

  const subscriberHasChanges = useMemo(() => {
    return (originalTerms?.memberIntroduction || '') !== (terms?.memberIntroduction || '')
  }, [originalTerms?.memberIntroduction, terms?.memberIntroduction])

  const guestHasChanges = useMemo(() => {
    if (!guestUserAccess) return false
    return originalGuestMessage !== guestMessage
  }, [guestUserAccess, originalGuestMessage, guestMessage])

  const hasChanges = subscriberHasChanges || guestHasChanges

  // Subscriber message is required (existing behavior). Guest message is
  // optional — backend treats empty as "use no custom message".
  const isValid = useMemo(() => subscriberMessage.trim().length > 0, [subscriberMessage])

  const handleCancel = useCallback(() => {
    setTerms(originalTerms)
    setGuestMessage(originalGuestMessage)
  }, [originalTerms, originalGuestMessage])

  useEffect(() => {
    registerUnsavedChangesChecker('draftWelcomeMessage', () => hasChanges, handleCancel)
  }, [registerUnsavedChangesChecker, hasChanges, handleCancel])

  const handleSave = async () => {
    if (!isValid || !hasChanges) return

    setIsPending(true)
    try {
      // Save in parallel; the two fields go through different endpoints. If
      // one fails the other can still succeed — we surface that by only
      // promoting "original" state for the field whose write resolved.
      const subscriberWrite = subscriberHasChanges && terms ? api.updateTerminology(terms) : Promise.resolve()
      const trimmedGuest = guestMessage.trim()
      const guestWrite = guestHasChanges
        ? api.updateGuestUserMessage(trimmedGuest.length > 0 ? trimmedGuest : null)
        : Promise.resolve()

      const [subscriberResult, guestResult] = await Promise.allSettled([subscriberWrite, guestWrite])

      const subscriberSaved = subscriberHasChanges && subscriberResult.status === 'fulfilled'
      const guestSaved = guestHasChanges && guestResult.status === 'fulfilled'
      const subscriberFailed = subscriberHasChanges && subscriberResult.status === 'rejected'
      const guestFailed = guestHasChanges && guestResult.status === 'rejected'

      if (subscriberSaved && terms) {
        setOriginalTerms(terms)
      }
      if (guestSaved) {
        setOriginalGuestMessage(trimmedGuest)
        setGuestMessage(trimmedGuest)
        // Other consumers of ['sponsor', 'info'] (e.g. useIsMediaMode) need to
        // see the new guestUserMessage value.
        await api.queryClient.invalidateQueries({ queryKey: ['sponsor', 'info'] })
      }

      if (subscriberHasChanges && subscriberResult.status === 'rejected') {
        console.error('Error saving subscriber welcome message:', subscriberResult.reason)
      }
      if (guestHasChanges && guestResult.status === 'rejected') {
        console.error('Error saving guest welcome message:', guestResult.reason)
      }

      if (subscriberFailed && guestFailed) {
        notifications.show({
          title: 'Save Failed',
          message: 'Could not save welcome messages. Please try again.',
          color: 'red',
        })
      } else if (subscriberFailed) {
        notifications.show({
          title: 'Save Failed',
          message: 'Could not save the subscriber welcome message. Please try again.',
          color: 'red',
        })
      } else if (guestFailed) {
        notifications.show({
          title: 'Save Failed',
          message: 'Could not save the guest welcome message. Please try again.',
          color: 'red',
        })
      } else if (subscriberSaved || guestSaved) {
        notifications.show({
          title: 'Welcome Message Saved',
          message: 'Your changes have been saved.',
          color: 'green',
        })
      }
    } finally {
      setIsPending(false)
    }
  }

  const handleSubscriberMessageChange = (value: string) => {
    setTerms(prev => {
      if (prev == null) return null
      return { ...prev, memberIntroduction: value }
    })
  }

  if (isLoading || anyQueryError || !allQueriesCompleted) {
    return (
      <Stack>
        <Paper radius="md" p="lg" shadow="sm" px="xl" bg="var(--mantine-color-gray-0)">
          <Group justify="space-between" align="center" p="md">
            <Title order={4}>Draft Welcome Message</Title>
            {isLoading && <Text>Loading...</Text>}
            {anyQueryError && <Text>Error loading data</Text>}
            {!allQueriesCompleted && !isLoading && !anyQueryError && <Text>Waiting for data to load...</Text>}
          </Group>
        </Paper>
      </Stack>
    )
  }

  return (
    <Stack>
      <Paper radius="md" p={isMobile ? 'md' : 'lg'} shadow="sm" px={isMobile ? 'md' : 'xl'}>
        <Group justify="space-between" mb="md">
          <Title order={isMobile ? 6 : 4} mb={isMobile ? 'md' : undefined}>
            Draft Welcome Message
          </Title>
          <Group>
            <Button variant="outline" onClick={handleCancel} disabled={!hasChanges || isPending}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!isValid || !hasChanges || isPending} loading={isPending}>
              Save
            </Button>
          </Group>
        </Group>

        {guestUserAccess && (
          <Stack mb="xl" gap="xs">
            <Title order={isMobile ? 6 : 5}>Guest Welcome Message</Title>
            <Text size="md" c="dimmed">
              Customize the welcome message shown to guest users who haven&apos;t subscribed yet when they first open
              your channel in the Sidekick Platform mobile app.
            </Text>
            <Textarea
              value={guestMessage}
              onChange={e => setGuestMessage(e.target.value)}
              placeholder="Welcome message content"
              minRows={6}
              maxRows={isMobile ? 30 : 10}
              autosize
              disabled={isPending}
              styles={{ input: { fontSize: '14px', lineHeight: '1.5' } }}
            />
          </Stack>
        )}

        <Stack gap="xs">
          {guestUserAccess && <Title order={isMobile ? 6 : 5}>Subscriber Welcome Message</Title>}
          {showTutorialText && (
            <Text size="md" c="dimmed">
              Customize the welcome message your end users will receive from their Personal Sidekick when they first log
              in to the Sidekick Platform mobile app.
            </Text>
          )}
          <Textarea
            value={subscriberMessage}
            onChange={e => handleSubscriberMessageChange(e.target.value)}
            placeholder="Welcome message content"
            minRows={6}
            maxRows={isMobile ? 30 : 10}
            autosize
            disabled={isPending}
            styles={{ input: { fontSize: '14px', lineHeight: '1.5' } }}
          />
        </Stack>
      </Paper>
    </Stack>
  )
}
